import { ApiErrorMessage, BaseApi } from "api";
import { endpoints } from "config";
import {
  GetCalibrationParams,
  SetCalibrationPayload,
  GetCalibrationResponse,
  SetCalibrationResponse,
} from "./calibrationModels";

class CalibrationApi extends BaseApi {
  /**
   * Parse errors from `getCalibration`
   */
  private static getCalibrationParseError: typeof this.parseGenericErrorMessageCode =
    (msg) => {
      switch (msg) {
        // API-specific error codes here, generic messages handled below
        default:
          return this.parseGenericErrorMessageCode(msg);
      }
    };

  /**
   * Get the existing calibration values for the panorama, this is used to set
   * up the form which will be set back to `setCalibration`
   */
  static getCalibration = async (params: GetCalibrationParams) => {
    try {
      const result = await this.makeRequest<
        GetCalibrationResponse,
        ApiErrorMessage[]
      >({
        url: endpoints.features.getCalibration,
        method: "GET",
        config: {
          params,
        },
      });
      if (this.isResponseSuccessful(result)) {
        return this.successResponse(result.data.details);
      } else {
        return this.errorResponse(
          this.getCalibrationParseError(result.data.errors[0].message_code)
        );
      }
    } catch (err: unknown) {
      return this.safelyCatchUnknownError<GetCalibrationResponse>(
        err,
        "getCalibration",
        this.getCalibrationParseError
      );
    }
  };

  /**
   * Parse errors from `setCalibration`
   */
  private static setCalibrationParseError: typeof this.parseGenericErrorMessageCode =
    (msg) => {
      switch (msg) {
        // API-specific error codes here, generic messages handled below
        case "Required":
          return "Required fields are missing";
        default:
          return this.parseGenericErrorMessageCode(msg);
      }
    };

  /**
   * Set new calibration values for the panorama
   */
  static setCalibration = async (payload: SetCalibrationPayload) => {
    try {
      const result = await this.makeRequest<
        SetCalibrationResponse,
        ApiErrorMessage[],
        SetCalibrationPayload
      >({
        url: endpoints.features.setCalibration,
        method: "POST",
        payload,
      });
      if (this.isResponseSuccessful(result)) {
        return this.successResponse(result.data.details);
      } else {
        return this.errorResponse(
          this.setCalibrationParseError(result.data.errors[0].message_code)
        );
      }
    } catch (err: unknown) {
      return this.safelyCatchUnknownError<SetCalibrationResponse>(
        err,
        "setCalibration",
        this.setCalibrationParseError
      );
    }
  };
}

export default CalibrationApi;
